import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createTimeline, stagger } from 'animejs'
import { IGNITION_SFX_TUNING } from '../config/ignitionSfx'
import { IGNITION_TUNING } from '../config/ignitionTuning'
import { SceneErrorBoundary } from './SceneErrorBoundary'
import '../styles/scene-loading.css'
import {
  dispatchSfxLoopVisibility,
  dispatchSfxOneShot,
} from '../utils/sfxEvents'

const IgnitionScene = lazy(() => import('../three/IgnitionScene.lazy'))

type IgnitionSequenceProps = {
  motionEnabled: boolean
  sceneEnabled: boolean
}

type IgnitionState = 'idle' | 'primed' | 'opening' | 'energized'

function IgnitionFallback() {
  return (
    <div
      className="ignition-fallback"
      aria-hidden="true"
    >
      <span className="ignition-fallback__body" />
      <span className="ignition-fallback__lid" />
      <span className="ignition-fallback__rim" />
      <span className="ignition-fallback__aperture" />
      <span className="ignition-fallback__seal" />
      <span className="ignition-fallback__tab-pivot">
        <span className="ignition-fallback__tab" />
      </span>
      <span className="ignition-fallback__beam" />
      <span className="ignition-fallback__core" />
    </div>
  )
}

function Callout({
  className,
  title,
  detail,
}: {
  className: string
  title: string
  detail: string
}) {
  return (
    <div className={`ignition-callout ${className}`}>
      <span className="ignition-callout__target" />
      <span className="ignition-callout__rule" />
      <span className="ignition-callout__copy">
        <strong>{title}</strong>
        <small>{detail}</small>
      </span>
    </div>
  )
}

export function IgnitionSequence({
  motionEnabled,
  sceneEnabled,
}: IgnitionSequenceProps) {
  const rootRef = useRef<HTMLElement>(null)
  const holdTimerRef = useRef(0)
  const resetTimerRef = useRef(0)
  const energizedTimerRef = useRef(0)
  const beamStartedRef = useRef(false)
  const stateRef = useRef<IgnitionState>('idle')
  const [ignitionState, setIgnitionState] = useState<IgnitionState>('idle')
  const [sceneRequested, setSceneRequested] = useState(false)
  const [sceneReady, setSceneReady] = useState(false)
  const [sceneFailed, setSceneFailed] = useState(false)

  const handleSceneReady = useCallback(() => {
    setSceneReady(true)
    setSceneFailed(false)
  }, [])

  const handleSceneFailure = useCallback(() => {
    setSceneReady(false)
    setSceneFailed(true)
  }, [])

  const updateState = useCallback((nextState: IgnitionState) => {
    stateRef.current = nextState
    setIgnitionState(nextState)
  }, [])

  const clearHoldTimer = useCallback(() => {
    window.clearTimeout(holdTimerRef.current)
    holdTimerRef.current = 0
  }, [])

  const clearResetTimer = useCallback(() => {
    window.clearTimeout(resetTimerRef.current)
    resetTimerRef.current = 0
  }, [])

  useEffect(() => {
    if (!sceneRequested || sceneReady || sceneFailed || !sceneEnabled) return

    // A missing asset must not hold the release sequence indefinitely.
    const timer = window.setTimeout(handleSceneFailure, 25_000)
    return () => window.clearTimeout(timer)
  }, [sceneRequested, sceneReady, sceneFailed, sceneEnabled, handleSceneFailure])

  useEffect(() => {
    const section = rootRef.current
    if (!section) return

    let frame = 0

    const measureVisibility = () => {
      frame = 0
      const bounds = section.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const visibleHeight = Math.max(
        0,
        Math.min(bounds.bottom, viewportHeight) - Math.max(bounds.top, 0),
      )
      const presentationHeight = Math.min(bounds.height, viewportHeight)
      const visibleRatio =
        presentationHeight > 0 ? visibleHeight / presentationHeight : 0

      if (
        sceneEnabled &&
        bounds.top < viewportHeight * 1.6 &&
        bounds.bottom > -viewportHeight * 0.35
      ) {
        setSceneRequested(true)
      }

      if (visibleRatio >= IGNITION_TUNING.fullViewRatio) {
        clearResetTimer()
        if (stateRef.current === 'idle') {
          updateState('primed')
        }
        // Start the one-second hold only once the actual articulated can is
        // visible, not while the FBX is still downloading or being parsed.
        if (sceneEnabled && !sceneFailed && !sceneReady) {
          clearHoldTimer()
          return
        }
        if (
          (stateRef.current === 'idle' || stateRef.current === 'primed') &&
          !holdTimerRef.current
        ) {
          clearHoldTimer()
          holdTimerRef.current = window.setTimeout(() => {
            holdTimerRef.current = 0
            updateState('opening')
            window.clearTimeout(energizedTimerRef.current)
            energizedTimerRef.current = window.setTimeout(() => {
              energizedTimerRef.current = 0
              updateState('energized')
            }, IGNITION_TUNING.energizedStateDelayMs)
          }, IGNITION_TUNING.fullViewHoldMs)
        }
        return
      }

      if (stateRef.current === 'primed') {
        clearHoldTimer()
        updateState('idle')
      }

      if (
        visibleRatio < IGNITION_TUNING.resetViewRatio &&
        (stateRef.current === 'opening' || stateRef.current === 'energized') &&
        !resetTimerRef.current
      ) {
        resetTimerRef.current = window.setTimeout(() => {
          resetTimerRef.current = 0
          window.clearTimeout(energizedTimerRef.current)
          energizedTimerRef.current = 0
          updateState('idle')
        }, IGNITION_TUNING.resetDelayMs)
      } else if (visibleRatio >= IGNITION_TUNING.resetViewRatio) {
        clearResetTimer()
      }
    }

    const requestMeasure = () => {
      if (!frame) frame = window.requestAnimationFrame(measureVisibility)
    }

    measureVisibility()
    window.addEventListener('scroll', requestMeasure, { passive: true })
    window.addEventListener('resize', requestMeasure)

    return () => {
      window.removeEventListener('scroll', requestMeasure)
      window.removeEventListener('resize', requestMeasure)
      if (frame) window.cancelAnimationFrame(frame)
      clearHoldTimer()
      clearResetTimer()
      window.clearTimeout(energizedTimerRef.current)
    }
  }, [
    clearHoldTimer,
    clearResetTimer,
    sceneEnabled,
    sceneFailed,
    sceneReady,
    updateState,
  ])

  useEffect(() => {
    if (ignitionState !== 'idle') return
    const root = rootRef.current
    if (!root) return

    root
      .querySelectorAll<HTMLElement>(
        '.ignition__sequence-item, .ignition-callout__target, .ignition-callout__rule, .ignition-callout__copy, .ignition__log-state',
      )
      .forEach((element) => {
        ['opacity', 'transform', 'translate', 'scale', 'color'].forEach(
          (property) => element.style.removeProperty(property),
        )
      })
  }, [ignitionState])

  const presentationActive = ignitionState !== 'idle'

  useEffect(() => {
    if (!presentationActive || !motionEnabled) return
    const root = rootRef.current
    if (!root) return

    const sequenceItems = root.querySelectorAll<HTMLElement>(
      '.ignition__sequence-item',
    )
    const timeline = createTimeline({ defaults: { ease: 'outExpo' } }).add(
      sequenceItems,
      {
        opacity: [0, 1],
        y: ['0.8rem', '0rem'],
        duration: 520,
        delay: stagger(100),
      },
    )

    return () => {
      timeline.cancel()
    }
  }, [presentationActive, motionEnabled])

  useEffect(() => {
    if (ignitionState !== 'opening' || !motionEnabled) return
    const root = rootRef.current
    if (!root) return

    const calloutTargets = root.querySelectorAll<HTMLElement>(
      '.ignition-callout__target',
    )
    const calloutRules = root.querySelectorAll<HTMLElement>(
      '.ignition-callout__rule',
    )
    const calloutCopy = root.querySelectorAll<HTMLElement>(
      '.ignition-callout__copy',
    )
    const logState = root.querySelector<HTMLElement>('.ignition__log-state')

    const timeline = createTimeline({ defaults: { ease: 'outExpo' } })
      .add(calloutTargets, {
        opacity: [0.28, 1],
        scale: [0.65, 1],
        duration: 520,
        delay: stagger(105),
      })
      .add(
        calloutCopy,
        {
          opacity: [0, 1],
          x: ['-0.55rem', '0rem'],
          duration: 520,
          delay: stagger(105),
        },
        '-=620',
      )
      .add(
        calloutRules,
        {
          opacity: [0.24, 0.92],
          scaleX: [0.16, 1],
          duration: 680,
          delay: stagger(90),
        },
        '-=360',
      )

    if (logState) {
      timeline.add(
        logState,
        {
          opacity: [0.45, 1],
          color: ['#aaa4b8', '#c7a8ff'],
          duration: 720,
        },
        '-=420',
      )
    }

    return () => {
      timeline.cancel()
    }
  }, [ignitionState, motionEnabled])

  const isIgnited =
    ignitionState === 'opening' || ignitionState === 'energized'

  useEffect(() => {
    const section = rootRef.current
    const tuning = IGNITION_SFX_TUNING.tab
    if (!section || !tuning.enabled || ignitionState !== 'opening') return

    const timer = window.setTimeout(() => {
      dispatchSfxOneShot(section, {
        src: tuning.src,
        volume: tuning.volume,
        playbackRate: tuning.playbackRate,
      })
    }, tuning.delayMs)

    return () => window.clearTimeout(timer)
  }, [ignitionState])

  useEffect(() => {
    const section = rootRef.current
    const tuning = IGNITION_SFX_TUNING.beam
    if (!section || !tuning.enabled) return

    const stopBeam = () => {
      if (!beamStartedRef.current) return
      beamStartedRef.current = false
      dispatchSfxLoopVisibility(section, {
        id: tuning.id,
        src: tuning.src,
        visible: false,
        volume: tuning.volume,
        fadeInMs: tuning.fadeInMs,
        fadeOutMs: tuning.fadeOutMs,
      })
    }

    if (!isIgnited || !motionEnabled) {
      stopBeam()
      return
    }

    const timer = window.setTimeout(() => {
      beamStartedRef.current = true
      dispatchSfxLoopVisibility(section, {
        id: tuning.id,
        src: tuning.src,
        visible: true,
        volume: tuning.volume,
        fadeInMs: tuning.fadeInMs,
        fadeOutMs: tuning.fadeOutMs,
      })
    }, tuning.delayMs)

    return () => {
      window.clearTimeout(timer)
      stopBeam()
    }
  }, [isIgnited, motionEnabled])

  return (
    <section
      ref={rootRef}
      className="ignition section"
      id="ignition"
      aria-labelledby="ignition-title"
      data-ignition-state={ignitionState}
      data-scene-ready={sceneReady ? 'true' : 'false'}
      data-scene-loading={sceneEnabled && !sceneReady && !sceneFailed ? 'true' : 'false'}
    >
      <div className="ignition__starfield" aria-hidden="true" />

      <header className="ignition__header">
        <p className="ignition__sequence-item">
          <span>NOVA ENERGY</span> // PRESSURE RELEASE PROTOCOL
        </p>
        <p className="ignition__sequence-item">RESEARCH DIVISION / 02</p>
      </header>

      <div className="ignition__layout">
        <div className="ignition__visual">
          <div
            className="ignition__scene-shell"
            aria-busy={sceneEnabled && !sceneReady && !sceneFailed}
          >
            {!sceneEnabled || sceneFailed ? <IgnitionFallback /> : null}
            {sceneEnabled && sceneRequested && !sceneFailed ? (
              <SceneErrorBoundary onFailure={handleSceneFailure}>
                <Suspense fallback={null}>
                  <IgnitionScene
                    ignited={isIgnited}
                    motionEnabled={motionEnabled}
                    onReady={handleSceneReady}
                    onFailure={handleSceneFailure}
                  />
                </Suspense>
              </SceneErrorBoundary>
            ) : null}
          </div>

          {sceneEnabled && sceneRequested && !sceneFailed ? (
            <div
              className={`scene-loading${sceneReady ? ' scene-loading--complete' : ''}`}
              role="status"
              aria-live="polite"
              aria-hidden={sceneReady}
            >
              <span className="scene-loading__orbit" aria-hidden="true">
                <span />
              </span>
              <span className="scene-loading__eyebrow">RELEASE PROTOCOL / 02</span>
              <span className="scene-loading__title">
                {sceneReady ? 'Artifact ready' : 'Receiving artifact…'}
              </span>
              <span className="scene-loading__detail">Model + surface data</span>
              <span className="scene-loading__track" aria-hidden="true" />
            </div>
          ) : null}
          {sceneFailed ? (
            <p className="scene-loading__fallback-note" role="status">
              Static view / 3D signal unavailable
            </p>
          ) : null}

          <div className="ignition__reticle ignition__reticle--wide" />
          <div className="ignition__reticle ignition__reticle--tight" />
          <Callout
            className="ignition-callout--ascent"
            title="ASCENT COLUMN"
            detail="IONIZED UPLIFT / NVA-03"
          />
          <Callout
            className="ignition-callout--pressure"
            title="PRESSURE EXIT"
            detail="SEALED FIELD / OPEN"
          />
          <Callout
            className="ignition-callout--seal"
            title="SEAL FRACTURE"
            detail="TAB LIFT / THRESHOLD"
          />
        </div>

        <div className="ignition__content">
          <p className="signal-label ignition__sequence-item">
            NOVA ENERGY // RELEASE EVENT
          </p>
          <h2 id="ignition-title">
            <span className="ignition__sequence-item">Break the seal.</span>
            <span className="ignition__sequence-item">Let the sky out.</span>
          </h2>
          <div
            className="ignition__divider ignition__sequence-item"
            aria-hidden="true"
          >
            <span />
          </div>
          <p className="ignition__lede ignition__sequence-item">
            One pull fractures the seal. Stored pressure turns into ultraviolet
            ascent — and the signal inside finds its way back to the sky.
          </p>

          <dl className="ignition__specs">
            <div className="ignition__spec-row ignition__sequence-item">
              <dt>Trigger</dt>
              <dd>Pull-tab fracture</dd>
            </div>
            <div className="ignition__spec-row ignition__sequence-item">
              <dt>Release</dt>
              <dd>Ultraviolet pressure</dd>
            </div>
            <div className="ignition__spec-row ignition__sequence-item">
              <dt>Effect</dt>
              <dd>Rapid signal ascent</dd>
            </div>
            <div className="ignition__spec-row ignition__sequence-item">
              <dt>Use</dt>
              <dd>Late sets / long edits</dd>
            </div>
          </dl>
        </div>
      </div>

      <footer className="ignition__footer" aria-hidden="true">
        <p>
          LOG: NVA-01-IGNITE <span className="ignition__log-state">// SEAL: {isIgnited ? 'OPEN' : 'CLOSED'}</span>
        </p>
        <span className="ignition__footer-line" />
        <p>CLASSIFIED RESEARCH&nbsp;&nbsp;• • •</p>
      </footer>
    </section>
  )
}
